import { cache } from 'react';

import { createServerApi } from '@/lib/server-api';
import {
  resolveFandomSlugLookupStatus,
  type FandomSlugListSnapshot,
  type FandomSlugLookupStatus,
} from './resolve-fandom-slug-lookup-status.util';

const SLUG_LOOKUP_LIMIT = '100';

type SlugRow = { slug?: string | null };

type SlugListPayload = {
  fandoms?: SlugRow[];
  universes?: SlugRow[];
  totalDocs?: number;
};

const toSnapshot = (
  rows: SlugRow[] | undefined,
  totalDocs: number | undefined,
): FandomSlugListSnapshot => {
  const safeRows = rows ?? [];
  return {
    rows: safeRows,
    totalDocs: totalDocs ?? safeRows.length,
  };
};

/**
 * Exact slug check against fandom and universe catalogs.
 * `missing` means both result sets were complete and neither slug matched.
 * Request failures and truncated searches stay `unavailable` so a blip does not 404 a real fandom.
 */
export const lookupFandomSlugStatus = cache(
  async (slug: string): Promise<FandomSlugLookupStatus> => {
    const normalized = slug.trim();
    if (!normalized) return 'missing';
    try {
      const serverApi = await createServerApi();
      const fandomParams = new URLSearchParams({
        search: normalized,
        limit: SLUG_LOOKUP_LIMIT,
        type: 'fanfic',
      });
      const universeParams = new URLSearchParams({
        search: normalized,
        limit: SLUG_LOOKUP_LIMIT,
        type: 'fanfic',
      });
      const [fandomsResponse, universesResponse] = await Promise.all([
        serverApi.get<SlugListPayload>(`/api/fandoms-custom?${fandomParams.toString()}`),
        serverApi.get<SlugListPayload>(`/api/universes-custom?${universeParams.toString()}`),
      ]);
      return resolveFandomSlugLookupStatus(normalized, [
        toSnapshot(fandomsResponse.data?.fandoms, fandomsResponse.data?.totalDocs),
        toSnapshot(universesResponse.data?.universes, universesResponse.data?.totalDocs),
      ]);
    } catch {
      return 'unavailable';
    }
  },
);
